import { useEffect, useState } from "react";
import { type IUpload } from "../libs/dragdrop/DragAndDrop";
import { Button, DialogActions, IconButton } from "@mui/material";
import { CheckOutlined, CloseOutlined } from "@mui/icons-material";
import { FMFile } from "../shared/FMFile";
import Dirs, { GDir } from "../fmanlibs/Dirs";
import Loading from "../libs/loading/Loading";
import Utils from "../libs/Utils";
import Files from "../fmanlibs/Files";
import { useDialog } from "../libs/dialog/DialogProvider";
import { TConfligResolution } from "../fmanlibs/FManApi";

export interface IUploadProps {
    root:FMFile
    files:IUpload[]
}

const CHUNK = 1024*100

async function loadFile(file:File) : Promise<Uint8Array>{
    return new Promise((success,fail) => {
        const reader = new FileReader()
        reader.onload = (e:any) => success(new Uint8Array(e.target.result))
        reader.onerror = fail
        reader.readAsArrayBuffer(file)
    })
}

function Upload(props:{root:FMFile, file:IUpload, onComplete:Function}) {
    const {file, onComplete} = props
    const [progress,setProgress] = useState<number>(0)
    const [root,setRoot] = useState<GDir|null>(null)
    const [error,setError] = useState<string>("")
    const [completed,setCompleted] = useState<boolean>(false)
    const [isDuplicate,setIsDuplicate] = useState<boolean>(false)

    const loadRoot = async () => {
        const rootDir = await Dirs.i.findPath(props.root._id, file.path)
        if (rootDir) {
            setRoot(rootDir)
            return rootDir
        }
        else throw `Path ${file.path} not found`
    }
    const upload = async (root:GDir, conflictResolution:TConfligResolution="none") => {
        const size = file.file.size
        let partial : FMFile
        try {
            setError("")
            setIsDuplicate(false)

            partial = await Files.i.createSession(root._id, file.file.name, size, conflictResolution)
            partial.parentId && Files.i.expire(partial.parentId).get(partial.parentId)
        }catch(e){
            if (Utils.httpStatus(e)==409) {
                setError("File already exits.")
                return setIsDuplicate(true)
            }
            else {
                return setError(Utils.errorMsg(e).message)
            }
        }

        try {
            const buffer = await loadFile(file.file)
            let offset = 0
            while(offset<size){
                const end = Math.min(offset+CHUNK, size)
                await Files.i.appendFile(partial._id, offset, buffer.slice(offset, end))
                offset += CHUNK
                setProgress(offset/size*100)
            }
            setCompleted(true)
        }catch(e){
            setError(Utils.errorMsg(e).message)
        }finally {
            onComplete()
        }
    }

    const onCopy = () => upload(root!, "rename")
    const onReplace = () => upload(root!, "replace")
    const onCancel = () => { setError("Cancelled"); onComplete() }

    useEffect(() => {loadRoot().then(upload).catch(e => setError(Utils.errorMsg(e).message))}, [])

    const style = {width:progress+"%"}
    const progressbar = root && !completed ? <div className="progress"><div className="bar" style={style}/></div> : null
    const conflict = isDuplicate ? <>
        <Button variant="text" onClick={onCopy}>Keep Both</Button>
        <Button variant="text" onClick={onReplace}>Replace</Button>
    </> : null
    const err = error ? <div className="error">{error}</div> : null
    return <div className="upload">
        <div className="flex middle gap-8">
            <div className="flex1">{root?.path}/{file.file.name}</div>
            {!root ? <Loading/> : null}
            {conflict}
            {completed ? <CheckOutlined/> : <IconButton size="small" onClick={onCancel}><CloseOutlined/></IconButton>}
        </div>
        {progressbar}{err}
    </div>
}

export default function UploadDlg(props:IUploadProps) {
    const [count,setCount] = useState<number>(0)
    const {closeDialog} = useDialog()
    const onCancel = () => closeDialog()
    const onComplete = () => setCount(c => {
        const total = c+1
        if (total==props.files.length) setTimeout(closeDialog, 100)
        return total
    })
    const uploads = props.files.map(file => <Upload root={props.root} file={file} key={file.path+"/"+file.file.name} onComplete={onComplete}/>)
    return <>
        <h2>Uploading {count+1}/{props.files.length}...</h2>
        <div className="content">
            {uploads}
        </div>
        <DialogActions>
            <Button variant="contained" color="primary" onClick={onCancel}>Cancel</Button>
        </DialogActions>
    </>
}